import { memo } from 'react';

/**
 * Props for the LoadingOverlay component
 */
export interface LoadingOverlayProps {
  /** Whether the overlay is visible */
  isVisible: boolean;
  /** Loading message to display */
  message?: string;
  /** Size of the spinner */
  size?: 'sm' | 'md' | 'lg';
  /** Custom className for additional styling */
  className?: string;
  /** Whether to blur the background */
  blur?: boolean;
}

/**
 * LoadingOverlay component
 * Provides a clean, modern loading overlay with minimal distraction
 */
function LoadingOverlayComponent({
  isVisible,
  message = 'Loading...',
  size = 'md',
  className = '',
  blur = true,
}: LoadingOverlayProps) {
  if (!isVisible) return null;

  const spinnerSizes = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
  };

  const textSizes = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 ${
        blur ? 'backdrop-blur-[2px]' : ''
      } ${className}`}
      role="loading"
      aria-label={message}
    >
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-xs mx-4 flex flex-col items-center space-y-6 border border-gray-100">
        {/* Modern Loading Spinner */}
        <div className="relative">
          <div
            className={`${spinnerSizes[size]} border-3 border-gray-200 rounded-full`}
          />
          <div
            className={`absolute top-0 left-0 ${spinnerSizes[size]} border-3 border-transparent border-t-blue-600 rounded-full animate-spin`}
            style={{ animationDuration: '1s' }}
          />
        </div>

        {/* Clean Loading Message */}
        <div className="text-center space-y-2">
          <p className={`font-semibold text-gray-800 ${textSizes[size]}`}>
            {message}
          </p>
          <div className="flex items-center justify-center space-x-1">
            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></div>
            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '200ms' }}></div>
            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '400ms' }}></div>
          </div>
        </div>

        {/* Progress indicator */}
        <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
          <div
            className="bg-blue-600 h-full rounded-full transition-all duration-1000 ease-in-out"
            style={{
              width: '60%',
              animation: 'slideProgress 1.5s infinite ease-in-out'
            }}
          ></div>
        </div>
      </div>

      {/* Global styles for custom animations */}
      <style>{`
        @keyframes slideProgress {
          0% { transform: translateX(-100%); opacity: 0.3; }
          50% { transform: translateX(0%); opacity: 1; }
          100% { transform: translateX(100%); opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

/**
 * Memoized LoadingOverlay component for optimal performance
 */
export const LoadingOverlay = memo(LoadingOverlayComponent) as typeof LoadingOverlayComponent;